import React from "react";
import { BreadcrumbsWrapper, Container, Alert, ConnectedAlert, ProgressBar } from "../../components";
import { Flex, Heading, Icon, IconName, Container as TeddyContainer, Text, Button } from "@telia/teddy";
import cn from "classnames";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import { connect, useDispatch } from "react-redux";
import { HolidayData, RootState } from "../../../../models";
import { UpdatePortingDateSubscription, UpdatePortingDateSubscriptionSimplified } from "../../../../models/state";
import {
    isOrderNotFound as isOrderNotFoundSelector,
    isOrderExpired as isOrderExpiredSelector,
    getErrorMessage,
} from "../../app/state/selectors";
import {
    isBankIdVerified as isBankIdVerifiedSelector,
    isBankIdPerformed as isBankIdPerformedSelector,
} from "../../authentication/state/selectors";
import BankIdSection from "../../checkout/components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
import { selectors as LOADING_SELECTORS } from "../../loading";
import PortingCardSimplified from "../components/atoms/PortingCardSimplified/PortingCardSimplified";
import PortingCard from "../components/molecules/PortingCard/PortingCard";
import { setSelectedDate, updatePortingDates } from "../state/actions";
import * as SELECTORS from "../state/selectors";
import TEXT from "../text-consts";
import "./UpdatePortingDate.less";

const crumbs = [
    { name: "Forsiden", link: "/" },
    { name: "Endre overføringsdato", link: "/mine-ordre/oppdater-portering/" },
];

interface UpdatePortingDatePerformedProps {
    description: string;
    failed: UpdatePortingDateSubscriptionSimplified[];
    icon?: IconName;
    success: UpdatePortingDateSubscriptionSimplified[];
    title: string;
}

const UpdatePortingDatePerformed: React.FC<UpdatePortingDatePerformedProps> = ({
    title,
    description,
    icon,
    success,
    failed,
}) => (
    <TeddyContainer className={"update-porting-date"}>
        <BreadcrumbsWrapper breadcrumbs={crumbs} />
        <Flex justify="center" align="center" direction="column" mt="100" mb="300">
            {icon && (
                <Icon
                    name={icon}
                    className={cn("update-porting-date__header-icon", {
                        "update-porting-date__header-icon--failed": _isEmpty(success),
                    })}
                    size="xl"
                    aria-hidden="true"
                    focusable="false"
                />
            )}
            <Heading mb="100" as="h1" variant="title-300" className="update-porting-date__header">
                {title}
            </Heading>
            {description && <Text mb="100">{description}</Text>}
        </Flex>
        <Container size="small">
            {success.map((subscription) => (
                <PortingCardSimplified
                    key={subscription.phoneNumber}
                    phoneNumber={subscription.phoneNumber}
                    date={subscription.date}
                />
            ))}
            {!_isEmpty(failed) && (
                <Text mb="100">
                    Vi kunne ikke oppdatere følgende telefonnummer. Vennligst prøv igjen. Dersom problemet vedvarer,
                    kontakt kundesenteret på telefon 92405050.
                </Text>
            )}
            {failed.map((subscription) => (
                <PortingCardSimplified
                    key={subscription.phoneNumber}
                    phoneNumber={subscription.phoneNumber}
                    date={subscription.date}
                />
            ))}
            {_isEmpty(failed) && (
                <div className="update-porting-date__submit-container">
                    <Button variant="primary" onClick={(): void => window.location.assign("/")}>
                        Tilbake til forsiden
                    </Button>
                </div>
            )}
            {!_isEmpty(failed) && (
                <div className="update-porting-date__submit-container">
                    <Button variant="primary" onClick={(): void => window.location.reload()}>
                        Tilbake
                    </Button>
                </div>
            )}
        </Container>
    </TeddyContainer>
);

interface getConfirmationTextsResponse {
    description: string;
    icon?: IconName;
    title: string;
}

const getConfirmationTexts = (
    success: UpdatePortingDateSubscriptionSimplified[],
    failed: UpdatePortingDateSubscriptionSimplified[],
): getConfirmationTextsResponse => {
    let icon: IconName | undefined = undefined;
    let title = "";
    let description = "";

    if (!_isEmpty(success) && _isEmpty(failed)) {
        icon = "check-circle-filled";
        title = "Ditt overføringstidspunkt er nå endret";
        description = "Hjertelig velkommen som Telia-kunde";
    } else if (!_isEmpty(success) && !_isEmpty(failed)) {
        icon = "check-circle-filled";
        title = "Ditt overføringstidspunkt er nå endret";
        description = "Hjertelig velkommen som Telia-kunde";
    } else if (_isEmpty(success) && !_isEmpty(failed)) {
        icon = "alert-filled";
        title = "Oisann! Her var det noe som gikk galt";
    }

    return { title, description, icon };
};

interface StateProps {
    bankIdRedirectUrl?: string;
    bankIdUrl?: string;
    creationDate: string | null;
    errorMessage?: string;
    hasPerformedRemoteUpdate: boolean;
    holidayData: HolidayData;
    isAllNewDatesEqual: boolean;
    isAtLeastOnePortInUpdateAvailable: boolean;
    isAtLeastOnePortInUpdated: boolean;
    isBankIdPerformed: boolean;
    isBankIdVerified: boolean;
    isErrorDoingAuthentication: boolean;
    isFetchingData: boolean;
    isOrderExpired: boolean;
    isOrderNotFound: boolean;
    isUpdatingDates: boolean;
    remoteUpdateFailed: UpdatePortingDateSubscriptionSimplified[];
    remoteUpdateInvalid: UpdatePortingDateSubscriptionSimplified[];
    remoteUpdateSuccess: UpdatePortingDateSubscriptionSimplified[];
    shouldShowBankId: boolean;
    subscriptions: UpdatePortingDateSubscription[];
}

interface UpdatePortingDateProps extends StateProps {
    customer?: string;
}

const UpdatePortingDate: React.FC<UpdatePortingDateProps> = ({
    // Redux (data)
    isBankIdVerified,
    creationDate,
    holidayData,
    hasPerformedRemoteUpdate,
    isAllNewDatesEqual,
    isAtLeastOnePortInUpdateAvailable,
    isAtLeastOnePortInUpdated,
    isUpdatingDates,
    remoteUpdateFailed,
    remoteUpdateInvalid,
    remoteUpdateSuccess,
    subscriptions,
    isFetchingData,
    isOrderExpired,
    isOrderNotFound,
    errorMessage,
}) => {
    const dispatch = useDispatch();

    if (isOrderNotFound || isOrderExpired || (errorMessage && !isBankIdVerified)) {
        const logicalErrorMessage = (() => {
            if (errorMessage && !_isEmpty(errorMessage)) return errorMessage;
            if (isOrderNotFound) return TEXT.ORDER_NOT_FOUND.TEXT;
            if (isOrderExpired) return TEXT.ORDER_EXPIRED.TEXT;
            return TEXT.ORDER_DEFAULT_ERROR.TEXT;
        })();
        return (
            <div className="update-porting-date">
                <BreadcrumbsWrapper breadcrumbs={crumbs} />
                <Flex justify="center" align="center" direction="column" mt="600" mb="600">
                    <Heading mb="100" as="h1" variant="title-500">
                        {TEXT.HEADING}
                    </Heading>
                </Flex>
                <Container size="medium">
                    <Alert kind="warning">{logicalErrorMessage}</Alert>
                </Container>
            </div>
        );
    }

    if (hasPerformedRemoteUpdate) {
        const { title, description, icon } = getConfirmationTexts(remoteUpdateSuccess, [
            ...remoteUpdateFailed,
            ...remoteUpdateInvalid,
        ]);
        return (
            <UpdatePortingDatePerformed
                title={title}
                description={description}
                icon={icon}
                success={remoteUpdateSuccess}
                failed={[...remoteUpdateFailed, ...remoteUpdateInvalid]}
            />
        );
    }

    return (
        <div className="update-porting-date">
            <div className="update-porting-date__heading-inner">
                <BreadcrumbsWrapper breadcrumbs={crumbs} />
                <ProgressBar isFetchingData={isFetchingData} standByColor="white" backgroundColor="white" />
            </div>
            <Container className="update-porting-date__bankid-container">
                <Heading mb="100" as="h1" variant="title-600">
                    {TEXT.HEADING}
                </Heading>
                {isAtLeastOnePortInUpdateAvailable && (
                    <Text mb="100">
                        Passer ikke overføringstidspunktet du har fått av oss? Endre det her ved å klikke på
                        kalender-ikon for å endre selve datoen og klikk på klokke-ikonet for å endre selve tidspunktet.
                    </Text>
                )}
                <Text mb="100">Du kan endre overføringstidspunktet inntil 2 timer før det er planlagt.</Text>
                <Text mb="100" variant="additional-100"></Text>
                <ConnectedAlert />
                <BankIdSection
                    typeId="upd_bankid"
                    referer="updatePortinDate"
                    skipDisplayHeading={true}
                    skipDisplayDescription={true}
                />
            </Container>
            {isBankIdVerified && (
                <Container size="small">
                    {errorMessage && <Alert kind="warning">{errorMessage}</Alert>}
                    {subscriptions.map((subscription, index) => {
                        const selectedDateString =
                            _get(subscription, "portingInfo.newDate") ||
                            _get(subscription, "portingInfo.actualDate", null) ||
                            _get(subscription, "portingInfo.estimatedDate", null) ||
                            creationDate ||
                            0;
                        const selectedDate = new Date(selectedDateString);
                        const shouldShowSameCheckBoxOnChanged = index === 0 && subscriptions.length > 1;
                        return (
                            <PortingCard
                                key={subscription.phoneNumber}
                                {...subscription}
                                holidayData={holidayData}
                                shouldShowSameCheckBoxOnChanged={shouldShowSameCheckBoxOnChanged}
                                shouldShowSameCheckBoxValue={isAllNewDatesEqual}
                                setSelectedDateForAllSubscriptions={(date) => {
                                    subscriptions.forEach((sub) => setSelectedDate(date, sub.guid));
                                }}
                                selectedDate={selectedDate}
                                setSelectedDate={(date) => dispatch(setSelectedDate(date, subscription.guid))}
                                creationDate={creationDate ?? undefined}
                                isEsim={subscription.isEsim}
                            />
                        );
                    })}
                    {isAtLeastOnePortInUpdateAvailable && (
                        <div className="update-porting-date__submit-container">
                            <Button
                                variant="primary"
                                disabled={!isAtLeastOnePortInUpdated || isUpdatingDates}
                                loading={isUpdatingDates}
                                onClick={() => {
                                    const orderItemGuid = subscriptions[0]?.guid; // Example: Use the first subscription's guid
                                    if (orderItemGuid) {
                                        dispatch(updatePortingDates(orderItemGuid));
                                    } else {
                                        //console.error("orderItemGuid is undefined");
                                    }
                                }}
                            >
                                Bekreft nye porteringstider
                            </Button>
                        </div>
                    )}
                </Container>
            )}
        </div>
    );
};

const mapStateToProps = (state: RootState): StateProps => ({
    isOrderNotFound: isOrderNotFoundSelector(state),
    isOrderExpired: isOrderExpiredSelector(state),
    errorMessage: getErrorMessage(state),
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    isBankIdVerified: isBankIdVerifiedSelector(state),
    isBankIdPerformed: isBankIdPerformedSelector(state),
    subscriptions: SELECTORS.getSubscriptions(state),
    creationDate: SELECTORS.getCreationDate(state) ?? null,
    isAllNewDatesEqual: SELECTORS.isAllNewDatesEqual(state),
    isAtLeastOnePortInUpdated: SELECTORS.isAtLeastOnePortInUpdated(state),
    isAtLeastOnePortInUpdateAvailable: SELECTORS.isAtLeastOnePortInUpdateAvailable(state),
    holidayData: SELECTORS.getHolidays(state),
    hasPerformedRemoteUpdate: SELECTORS.hasPerformedRemoteUpdate(state),
    remoteUpdateSuccess: SELECTORS.getSuccessfullyUpdatedDates(state),
    remoteUpdateFailed: SELECTORS.getFailedUpdatedDates(state),
    remoteUpdateInvalid: SELECTORS.getInvalidUpdatedDates(state),
    isErrorDoingAuthentication: false,
    isUpdatingDates: false,
    shouldShowBankId: false,
});

export default connect(mapStateToProps)(UpdatePortingDate);
